import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Building2, Users, MapPin, Plus } from 'lucide-react';

interface EndUser {
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  address_street: string;
  address_city: string;
  address_zip_code: string;
  address_country_region_id: string;
  segment_id?: string;
  industry: string;
  licenses: number;
  status: string;
  csn: string;
}

interface Country {
  name: string;
  value: string;
}

interface CreateEndUserDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  newEndUser: EndUser;
  onEndUserChange: (endUser: EndUser) => void;
  segments: string[];
  countries: Country[];
  onSubmit: () => void;
  onCancel: () => void;
}

export function CreateEndUserDialog({
  isOpen,
  onOpenChange,
  newEndUser,
  onEndUserChange,
  segments,
  countries,
  onSubmit,
  onCancel
}: CreateEndUserDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-worldsview-green" />
            Create New End User Account
          </DialogTitle>
          <DialogDescription>
            Add a new end user account that will be automatically selected for this opportunity.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Company Information */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Building2 className="w-4 h-4 text-worldsview-blue" />
              Company Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="company-name">
                  Company Name <span className="text-worldsview-red">*</span>
                </Label>
                <Input
                  id="company-name"
                  placeholder="Enter company name"
                  value={newEndUser.name}
                  onChange={(e) => onEndUserChange({ ...newEndUser, name: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <Select value={newEndUser.segment_id} onValueChange={(value) => onEndUserChange({ ...newEndUser, segment_id: value })}>
                  <SelectTrigger id="industry" className="glass-input">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {segments.map((segment) => (
                      <SelectItem key={segment} value={segment}>
                        {segment}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Primary Contact Information */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Users className="w-4 h-4 text-worldsview-blue" />
              Primary Contact Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact-first-name">
                  First Name <span className="text-worldsview-red">*</span>
                </Label>
                <Input
                  id="contact-first-name"
                  placeholder="First name"
                  value={newEndUser.first_name}
                  onChange={(e) => onEndUserChange({ ...newEndUser, first_name: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-last-name">
                  Last Name <span className="text-worldsview-red">*</span>
                </Label>
                <Input
                  id="contact-last-name"
                  placeholder="Last name"
                  value={newEndUser.last_name}
                  onChange={(e) => onEndUserChange({ ...newEndUser, last_name: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  Email <span className="text-worldsview-red">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@company.com"
                  value={newEndUser.email}
                  onChange={(e) => onEndUserChange({ ...newEndUser, email: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+27 (11) 000-0000"
                  value={newEndUser.phone_number}
                  onChange={(e) => onEndUserChange({ ...newEndUser, phone_number: e.target.value })}
                  className="glass-input"
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4 text-worldsview-blue" />
              Address Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  placeholder="123 Main Street"
                  value={newEndUser.address_street}
                  onChange={(e) => onEndUserChange({ ...newEndUser, address_street: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  placeholder="City"
                  value={newEndUser.address_city}
                  onChange={(e) => onEndUserChange({ ...newEndUser, address_city: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="postal-code">Postal Code</Label>
                <Input
                  id="postal-code"
                  placeholder="0000"
                  value={newEndUser.address_zip_code}
                  onChange={(e) => onEndUserChange({ ...newEndUser, address_zip_code: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Select value={newEndUser.address_country_region_id} onValueChange={(value) => onEndUserChange({ ...newEndUser, address_country_region_id: value })}>
                  <SelectTrigger id="country" className="glass-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.value} value={country.value}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t mt-6">
          <Button
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            className="bg-worldsview-green hover:bg-worldsview-green/90"
            onClick={onSubmit}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create and Add End user
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
